import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { environment } from '@/config/environment';
import { useAuth } from '@/hooks/useAuth';
import { useSupportUnreadCountQuery } from '@/hooks/useSupport';
import { SUPPORT_UNREAD_BADGE_MAXIMUM } from '@/constants/support';
import { cn } from '@/lib/utils';
import {
  ADMIN_NAVIGATION_ITEMS,
  isAdminNavigationItemVisible,
  type AdminNavigationItem,
} from './navigationItems';
import { ProfileMenu } from './ProfileMenu';
import { WorkspaceSwitcher } from '@/components/layout/WorkspaceSwitcher';

function buildUnreadBadgeLabel(unreadCount: number): string {
  return unreadCount > SUPPORT_UNREAD_BADGE_MAXIMUM
    ? String(SUPPORT_UNREAD_BADGE_MAXIMUM) + '+'
    : String(unreadCount);
}

export function AdminSidebar() {
  const { t } = useTranslation('admin');
  const { platformPermissions } = useAuth();
  const supportUnreadCountQuery = useSupportUnreadCountQuery('platform');
  const supportUnreadCount =
    supportUnreadCountQuery.data === undefined ? 0 : supportUnreadCountQuery.data;

  const visibleNavigationItems = ADMIN_NAVIGATION_ITEMS.filter(
    function keepPermittedItem(item: AdminNavigationItem) {
      return isAdminNavigationItemVisible(item, platformPermissions);
    }
  );

  return (
    <aside className="hidden md:flex w-60 bg-zooindigo flex-col shrink-0 h-full overflow-hidden">
      <div className="p-5 pb-4 flex items-center gap-2.5 border-b border-white/10">
        <span className="w-9 h-9 rounded-xl bg-zooprimary flex items-center justify-center">
          <Icon name="Shield" className="text-zoowhite" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-zoowhite text-sm leading-tight">{t('consoleName')}</div>
          <div className="text-[10px] text-white/50">{environment.brandName}</div>
        </div>
      </div>

      <div className="px-3 pt-3">
        <WorkspaceSwitcher tone="dark" />
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
        <div className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-white/40">
          {t('navigation.sectionLabel')}
        </div>
        {visibleNavigationItems.map(function renderNavigationLink(item) {
          return (
            <NavLink
              key={item.id}
              to={item.route}
              end={item.route === '/admin'}
              className={function resolveLinkClassName({ isActive }) {
                return cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors',
                  isActive === true
                    ? 'bg-zooprimary text-zoowhite font-semibold'
                    : 'text-white/60 hover:bg-white/10 hover:text-zoowhite'
                );
              }}
            >
              <Icon name={item.iconName} />
              <span className="flex-1 truncate">{t(item.labelKey)}</span>
              {item.unreadBadge === true && supportUnreadCount > 0 ? (
                <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-zooprimary text-zoowhite text-[10px] font-bold shrink-0">
                  {buildUnreadBadgeLabel(supportUnreadCount)}
                </span>
              ) : null}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-4 py-3 border-t border-white/10">
        <LanguageSwitcher tone="dark" />
      </div>

      <ProfileMenu />
    </aside>
  );
}
